import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductById } from "../../../RTK/productSlice";
import { deleteProduct } from "../../../RTK/productDetailsSlice";

// Import all popup components
import AgentPopup from "../product-popup/AgentPopup ";
import CreateAgentPopup from "../product-popup/CreateAgentPopup";
import EditProductPopup from "../product-popup/EditProductPopup ";
import ConfirmDeleteProduct from "../product-popup/ConfirmDeletProduct";
import { usePermissions } from "../../../contexts/PermissionsContext";
import { Lock } from "lucide-react";
import {
  ArrowLeft,
  X,
  Package,
  FileText,
  FolderOpen,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  UserPlus,
  Percent,
  Calendar,
  Users,
  Loader2,
  AlertCircle,
  Tag,
  Globe,
  Eye,
  EyeOff,
  Info,
  Star,
  Clock,
  Image as ImageIcon,
  BarChart3,
  DollarSign,
} from "lucide-react";
import { toast } from "react-toastify";

// Agents Popup Component (same as in original popup)
const AgentsPopup = ({ agents, onClose, onCreateAgent, onAgentClick }) => {
  const [multiplier, setMultiplier] = useState(1);

  if (!agents) return null;

  const sortedAgents = [...agents].sort((a, b) => {
    return a.agent_price * multiplier - b.agent_price * multiplier;
  });

  return (
    <div className="fixed inset-0 z-[3000]">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur"
        onClick={onClose}
      />
      <div
        className="w-[90%] max-w-lg bg-gray-900 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-xl shadow-2xl"
        dir="rtl"
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-[#FEC111]" />
            وكلاء المنتج
          </h2>
          <button
            onClick={onClose}
            className="text-white w-8 h-8 rounded-full flex items-center justify-center border border-gray-600 hover:bg-[#FEC111] hover:text-black hover:border-transparent transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <button
            onClick={onCreateAgent}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#FEC111] text-black rounded-lg hover:bg-[#FEC111]/90 transition-colors font-bold mb-4"
          >
            <UserPlus className="w-5 h-5" />
            إضافة وكيل جديد
          </button>

          <input
            type="number"
            min="1"
            className="w-full mb-6 px-4 py-2 rounded-lg bg-gray-800 text-white border border-gray-600 focus:outline-none focus:border-yellow-500"
            value={multiplier}
            onChange={(e) => setMultiplier(Number(e.target.value) || 1)}
            placeholder="أدخل رقم الضرب"
          />

          <div className="space-y-4">
            {sortedAgents.length > 0 ? (
              sortedAgents.map((agent, index) => (
                <button
                  key={agent.parent_id}
                  onClick={() => onAgentClick(agent)}
                  className={`w-full p-4 rounded-xl transition-all hover:scale-102 hover:shadow-lg relative
              ${
                agent.is_product_available
                  ? "bg-green-500/10 border border-green-500/20 hover:border-green-500/40"
                  : "bg-red-500/10 border border-red-500/20 hover:border-red-500/40"
              }`}
                >
                  {index === 0 && agent.is_product_available && (
                    <div className="absolute -top-2 -right-2 bg-[#FEC111] text-black text-xs px-2 py-1 rounded-full font-bold">
                      الأرخص
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold">
                        {agent.agent}
                      </span>
                      <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded-full">
                        #{index + 1}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-gray-400">
                        ID: #{agent.parent_id}
                      </span>
                      <span
                        className={`font-semibold ${
                          index === 0 && agent.is_product_available
                            ? "text-[#FEC111]"
                            : "text-white"
                        }`}
                      >
                        {(agent.agent_price * multiplier).toFixed(2)}$
                      </span>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="inline-block p-3 rounded-full bg-gray-800/50 mb-4">
                  <Users className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-400">
                  لا يوجد وكلاء متاحين لهذا المنتج
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Info Card Component
const InfoCard = ({ title, value, icon: Icon, color = "text-blue-400", className = "" }) => (
  <div className={`bg-gradient-to-br from-gray-700/30 to-gray-800/30 p-4 sm:p-6 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-300 hover:scale-[1.02] ${className}`}>
    <div className="flex items-center gap-3 mb-3">
      <div
        className={`p-2 bg-gray-800/50 rounded-lg ${color.replace(
          "text-",
          "text-"
        )}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-gray-300 font-medium">{title}</h3>
    </div>
    <p className="text-white text-lg font-semibold break-all">
      {value || "غير محدد"}
    </p>
  </div>
);

// Date Information Card - 
// const DateInfoCard = ({ selectedProduct }) => (
//   <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
//     <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
//       <Calendar className="w-5 h-5 text-green-500" />
//       معلومات التاريخ
//     </h2>
//     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
//       <InfoCard
//         title="تاريخ الإضافة"
//         value={selectedProduct.date_added || "غير محدد"}
//         icon={Calendar}
//         color="text-blue-400"
//       />
//       {selectedProduct.created_at && (
//         <InfoCard
//           title="تاريخ الإنشاء"
//           value={new Date(selectedProduct.created_at).toLocaleDateString("ar-EG")}
//           icon={Calendar}
//           color="text-green-400"
//         />
//       )}
//       {selectedProduct.updated_at && (
//         <InfoCard
//           title="آخر تحديث"
//           value={new Date(selectedProduct.updated_at).toLocaleDateString("ar-EG")}
//           icon={Clock}
//           color="text-yellow-400"
//         />
//       )}
//     </div>
//   </div>
// );

// Main ProductDetails Component
const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { hasPermission } = usePermissions();

  // ✅ فحص الصلاحيات
  const canUpdateProduct = hasPermission("update_product");
  const canDeleteProduct = hasPermission("delete_product");


  const { selectedProduct, loading, error } = useSelector(
    (state) => state.products
  );
  const [imageError, setImageError] = useState(false);

  // All popup states
  const [showAgentsPopup, setShowAgentsPopup] = useState(false);
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [showCreateAgentPopup, setShowCreateAgentPopup] = useState(false);
  const [showEditProductPopup, setShowEditProductPopup] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(id));
    }
  }, [id, dispatch]);

  const handleEditPopupClose = () => {
    setShowEditProductPopup(false);
    // Optionally refresh product data
    if (id) {
      dispatch(fetchProductById(id));
    }
  };
  
  const handleBack = () => {
    navigate("/product");
  };

  const handleManageAgents = () => {
    navigate(`/product/${id}/agents`);
  };

  const handleCreateAgent = () => {
    setShowCreateAgentPopup(true);
    setShowAgentsPopup(false);
  };

  const handleDeleteProduct = () => {
    if (!canDeleteProduct) {
      toast.error("ليس لديك صلاحية لحذف المنتجات 🔒");
      return;
    }
    setShowConfirmDelete(true);
  };

  const handleEditClick = () => {
    if (!canUpdateProduct) {
      toast.error("ليس لديك صلاحية لتعديل المنتجات 🔒");
      return;
    }
    setShowEditProductPopup(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteProduct(selectedProduct.id));
    setShowConfirmDelete(false);
    navigate("/product");
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "text-green-400 bg-green-400/10 border-green-400/20";
      case "unavailable":
        return "text-red-400 bg-red-400/10 border-red-400/20";
      default:
        return "text-gray-400 bg-gray-400/10 border-gray-400/20";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "متوفر";
      case "unavailable":
        return "غير متوفر";
      default:
        return status || "غير محدد";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-16 h-16 text-yellow-500 animate-spin" />
          <p className="text-white text-lg text-center">
            جاري تحميل تفاصيل المنتج...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !selectedProduct) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="bg-red-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-white text-xl mb-4">لم يتم العثور على المنتج</p>
          <p className="text-gray-400 mb-6">
            {error || "المنتج غير موجود أو تم حذفه"}
          </p>
          <button
            onClick={handleBack}
            className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-xl transition-colors font-medium"
          >
            العودة للمنتجات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
      dir="rtl"
    >
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={handleBack}
              className="p-2 sm:p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-xl border border-gray-700/50 transition-all duration-300 group flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-white transition-colors" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent truncate">
                {selectedProduct.name}
              </h1>
              <p className="text-gray-400 text-sm sm:text-base flex items-center gap-2 mt-1 sm:mt-2">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
                <span className="text-xs sm:text-sm">تفاصيل المنتج</span>
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl border font-medium text-sm sm:text-base flex-shrink-0 ${getStatusColor(
              selectedProduct.status
            )}`}
          >
            {selectedProduct.status?.toLowerCase() === "available" ? (
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            )}
            <span>{getStatusText(selectedProduct.status)}</span>
          </div>
        </div>

        {/* Main Content - محسن التخطيط */}
        <div className="space-y-6 sm:space-y-8">
          {/* Product Image and Basic Info - محسن */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 sm:gap-8">
            {/* Product Image and Description */}
            <div className="xl:col-span-1 space-y-6">
              {/* Product Image */}
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-yellow-500" />
                  صورة المنتج
                </h2>
                <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-800/50">
                  {selectedProduct.image && !imageError ? (
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <Package className="w-16 h-16 mb-2" />
                      <span className="text-sm">لا توجد صورة</span>
                    </div>
                  )}
                </div>
                {selectedProduct.alt_text && (
                  <p className="text-gray-400 text-sm mt-3 text-center">
                    {selectedProduct.alt_text}
                  </p>
                )}
              </div>

              {/* Description */}
              {selectedProduct.description && (
                <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-green-500" />
                    الوصف
                  </h2>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base whitespace-pre-line">
                    {selectedProduct.description}
                  </p>
                </div>
              )}
            </div>

            {/* Basic Information - موسع */}
            <div className="xl:col-span-3 space-y-6">
              {/* المعلومات الأساسية */}
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-500" />
                  المعلومات الأساسية
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <InfoCard
                    title="اسم المنتج"
                    value={selectedProduct.name}
                    icon={Tag}
                    color="text-blue-400"
                  />
                  <InfoCard
                    title="معرف المنتج"
                    value={`#${selectedProduct.id}`}
                    icon={Tag}
                    color="text-purple-400"
                  />
                  <InfoCard
                    title="السعر"
                    value={
                      selectedProduct.price
                        ? `$${parseFloat(selectedProduct.price).toFixed(4)}`
                        : "غير محدد"
                    }
                    icon={Tag}
                    color="text-yellow-400"
                  />
                  <InfoCard
                    title="نوع المنتج"
                    value={selectedProduct.product_type}
                    icon={Package}
                    color="text-indigo-400"
                  />
                  <InfoCard
                    title="الوكيل الأرخص"
                    value={selectedProduct.cheapest_agent_name || "غير محدد"}
                    icon={Users}
                    color="text-orange-400"
                  />
                  <InfoCard
                    title="معرف الوكيل"
                    value={selectedProduct.cheapest_parent_id ? `ID: ${selectedProduct.cheapest_parent_id}` : "غير محدد"}
                    icon={Tag}
                    color="text-cyan-400"
                  />
                </div>
              </div>

              {/* المعلومات الإضافية */}
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-500" />
                  معلومات إضافية
                </h2>

                {selectedProduct.is_fixed_price && (
                  <div className="mb-4 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 p-3 rounded-lg">
                    هذا المنتج يستخدم سعراً ثابتاً موحداً لجميع المستخدمين (مستثنى منه نسبه المستويات).
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  <InfoCard
                    title="سعر ثابت"
                    value={selectedProduct.is_fixed_price ? "مفعّل" : "غير مفعّل"}
                    icon={DollarSign}
                    color={selectedProduct.is_fixed_price ? "text-amber-400" : "text-gray-400"}
                  />
                  <InfoCard
                    title="التكلفة الثابتة"
                    value={
                      selectedProduct.is_fixed_price && selectedProduct.fixed_cost
                        ? `$${parseFloat(selectedProduct.fixed_cost).toFixed(4)}`
                        : "—"
                    }
                    icon={DollarSign}
                    color="text-amber-400"
                  />
                  <InfoCard
                    title="العمولة"
                    value={
                      selectedProduct.commission_rate
                        ? `${parseFloat(selectedProduct.commission_rate).toFixed(2)}%`
                        : "0.00%"
                    }
                    icon={Percent}
                    color="text-green-400"
                  />
                  <InfoCard
                    title="الرؤية"
                    value={selectedProduct.is_hidden ? "مخفي" : "مرئي"}
                    icon={selectedProduct.is_hidden ? EyeOff : Eye}
                    color={
                      selectedProduct.is_hidden
                        ? "text-red-400"
                        : "text-green-400"
                    }
                  />
                  <InfoCard
                    title="فوري"
                    value={selectedProduct.is_instant ? "نعم" : "لا"}
                    icon={Clock}
                    color={selectedProduct.is_instant ? "text-green-400" : "text-yellow-400"}
                  />
                  <InfoCard
                    title="متوفر"
                    value={selectedProduct.available ? "نعم" : "لا"}
                    icon={selectedProduct.available ? CheckCircle : XCircle}
                    color={selectedProduct.available ? "text-green-400" : "text-red-400"}
                  />
                  <InfoCard
                    title="فحص اللاعب"
                    value={selectedProduct.player_check ? "مفعل" : "غير مفعل"}
                    icon={selectedProduct.player_check ? CheckCircle : XCircle}
                    color={selectedProduct.player_check ? "text-green-400" : "text-gray-400"}
                  />
                  <InfoCard
                    title="التصنيف"
                    value={
                      selectedProduct.category?.[0]?.name ||
                      selectedProduct.category?.name ||
                      "غير مصنف"
                    }
                    icon={FolderOpen}
                    color="text-pink-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Product Requirements and Quantity Values - محسن */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Product Requirements */}
            {selectedProduct.product_requirements?.required && (
              <div className="lg:col-span-2 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                  المتطلبات المطلوبة
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProduct.product_requirements.required.map((requirement, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 p-3 sm:p-4 rounded-xl hover:border-blue-500/40 transition-colors"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="text-white font-medium text-sm sm:text-base">
                          {requirement}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Parameters */}
                {selectedProduct.params && selectedProduct.params.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Info className="w-4 h-4 text-gray-400" />
                      المعاملات
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {selectedProduct.params.map((param, index) => (
                        <div key={index} className="text-gray-300 text-sm bg-gray-700/30 p-3 rounded-lg border border-gray-600/30">
                          • {param}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quantity Values */}
            {selectedProduct.qty_values && (
              <div className="lg:col-span-1 bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-yellow-500" />
                  نطاق الكمية
                </h2>
                <div className="space-y-4">
                  <InfoCard
                    title="الحد الأدنى"
                    value={selectedProduct.qty_values.min ? selectedProduct.qty_values.min.toLocaleString() : "غير محدد"}
                    icon={ArrowLeft}
                    color="text-green-400"
                  />
                  <InfoCard
                    title="الحد الأقصى"
                    value={selectedProduct.qty_values.max ? selectedProduct.qty_values.max.toLocaleString() : "غير محدد"}
                    icon={ArrowLeft}
                    color="text-red-400"
                  />
                
                  {/* Additional Product Info */}
                  {/* <InfoCard
                    title="نوع البيانات"
                    value={selectedProduct.type || "غير محدد"}
                    icon={Tag}
                    color="text-purple-400"
                  />
                  {selectedProduct.slug && (
                    <InfoCard
                      title="الرابط المختصر"
                      value={selectedProduct.slug}
                      icon={Globe}
                      color="text-cyan-400"
                    />
                  )} */}
                </div>
              </div>
            )}
          </div>

          {/* Date Information - محدث */}
          {/* <DateInfoCard selectedProduct={selectedProduct} /> */}

          {/* SEO Information - محسن إذا وجد */}
          {(selectedProduct.seo_title || selectedProduct.seo_description) && (
            <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-500" />
                معلومات SEO
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                {selectedProduct.seo_title && (
                  <InfoCard
                    title="عنوان SEO"
                    value={selectedProduct.seo_title}
                    icon={Tag}
                    color="text-purple-400"
                  />
                )}
                {selectedProduct.seo_description && (
                  <InfoCard
                    title="وصف SEO"
                    value={selectedProduct.seo_description}
                    icon={Info}
                    color="text-indigo-400"
                  />
                )}
              </div>
            </div>
          )}

          {/* Services and Subcategories - محسن */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Services */}
            {selectedProduct.services && selectedProduct.services.length > 0 && (
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  الخدمات المتاحة
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProduct.services.map((service, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 p-3 sm:p-4 rounded-xl hover:border-yellow-500/40 transition-colors"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="bg-yellow-500/20 p-2 rounded-lg">
                          <Star className="w-4 h-4 text-yellow-400" />
                        </div>
                        <span className="text-white font-medium text-sm sm:text-base">
                          {service}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subcategories */}
            {selectedProduct.subcategories && selectedProduct.subcategories.length > 0 && (
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-blue-500" />
                  التصنيفات الفرعية
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProduct.subcategories.map((subcategory, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 p-3 sm:p-4 rounded-xl hover:border-blue-500/40 transition-colors"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="bg-blue-500/20 p-2 rounded-lg">
                          <FolderOpen className="w-4 h-4 text-blue-400" />
                        </div>
                        <span className="text-white font-medium text-sm sm:text-base">
                          {subcategory.name || subcategory}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons - محسن */}
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-500" />
              إجراءات المنتج
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {canUpdateProduct ? (
              <button
                onClick={handleEditClick}
                className="flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-yellow-500/20 text-yellow-400 rounded-xl hover:bg-yellow-500/30 transition-all duration-300 font-medium border border-yellow-500/30 hover:scale-[1.02]"
              >
                <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">تعديل المنتج</span>
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 px-4 py-3 sm:py-4 bg-slate-800 border border-slate-700 rounded-xl text-slate-400">
                <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">تعديل المنتج</span>
              </div>
            )}
              <button
                onClick={handleManageAgents}
                className="flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-all duration-300 font-medium border border-blue-500/30 hover:scale-[1.02]"
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">إدارة الوكلاء</span>
              </button>
              <button
                onClick={() => navigate(`/product/${id}/analytics`)}
                className="flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-purple-500/20 text-purple-400 rounded-xl hover:bg-purple-500/30 transition-all duration-300 font-medium border border-purple-500/30 hover:scale-[1.02]"
              >
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">التحليلات</span>
              </button>
              {canDeleteProduct ? (
              <button
                onClick={handleDeleteProduct}
                className="flex items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:py-4 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-all duration-300 font-medium border border-red-500/30 hover:scale-[1.02]"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">حذف المنتج</span>
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 px-4 py-3 sm:py-4 bg-slate-800 border border-slate-700 rounded-xl text-slate-400">
                <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">حذف المنتج</span>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* All Popups - existing code remains the same */}
      {showAgentPopup && (
        <AgentPopup
          agent={selectedAgent}
          setShowAgentPopup={setShowAgentPopup}
        />
      )}

      {showCreateAgentPopup && (
        <CreateAgentPopup
          setShowPopup={setShowCreateAgentPopup}
          productId={selectedProduct?.id}
          agents={selectedProduct.agents}
          agent={selectedProduct.agent}
        />
      )}

      {/* ✅ Edit Popup - يظهر فقط لو عنده صلاحية */}
      {showEditProductPopup && canUpdateProduct && (
        <EditProductPopup
          product={selectedProduct}
          setShowEditProductPopup={handleEditPopupClose}
        />
      )}

      {/* ✅ Delete Confirmation - يظهر فقط لو عنده صلاحية */}
      {showConfirmDelete && canDeleteProduct && (
        <ConfirmDeleteProduct
          isOpen={showConfirmDelete}
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default ProductDetails;
